import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EstimateProvider, useEstimate } from './store/EstimateContext';
import Header from './components/Header';
import DataModulesTab from './components/DataModulesTab';
import IllustrationsTab from './components/IllustrationsTab';
import DmlTab from './components/DmlTab';
import RolesTab from './components/RolesTab';
import ResourcingTab from './components/ResourcingTab';
import BrexTab from './components/BrexTab';
import ReadinessTab from './components/ReadinessTab';
import SummaryTab from './components/SummaryTab';
import ProjectsTab from './components/ProjectsTab';
import CompareTab from './components/CompareTab';
import RateCardsTab from './components/RateCardsTab';
import RiskTab from './components/RiskTab';
import ScheduleTab from './components/ScheduleTab';
import BidPriceTab from './components/BidPriceTab';
import PortfolioTab from './components/PortfolioTab';
import SystemBreakdownTab from './components/SystemBreakdownTab';
import ReviewsUpdatesTab from './components/ReviewsUpdatesTab';
import { fmtHours } from './lib/calculations';
import { ArrowLeft } from 'lucide-react';
import './i18n';

function FooterBar() {
  const { totals, resourcing, combined, formatCurrency } = useEstimate();
  return (
    <div
      className="flex items-center justify-between bg-white border border-[#E7E6E2] rounded-xl px-5 py-3.5 mt-5 text-[13px] flex-wrap gap-3"
      data-testid="estimator-footer-bar"
    >
      <div className="flex items-center gap-5 flex-wrap text-[#5F5E5A]">
        <span data-testid="estimator-footer-total-hours">
          Total hours <b className="text-[#111]">{fmtHours(combined.combinedHours)}</b>
        </span>
        <span data-testid="estimator-footer-blended-rate">
          Blended rate <b className="text-[#111]">{formatCurrency(totals.blendedRate)}/hr</b>
        </span>
        <span data-testid="estimator-footer-modules">
          Modules/illus <b className="text-[#111]">{totals.totalDms} / {totals.totalIllus}</b>
        </span>
        <span data-testid="estimator-footer-fte">
          FTE <b className="text-[#111]">{(resourcing?.fteEffective || 0).toFixed(2)} · {resourcing?.workingDays || 0}d</b>
        </span>
        <span data-testid="estimator-footer-base-cost">
          Base <b className="text-[#111]">{formatCurrency(combined.combinedBaseCost)}</b>
        </span>
      </div>
      <div className="text-xl font-extrabold text-[#5A2FC7]" data-testid="estimator-footer-total-cost">
        Grand total {formatCurrency(combined.combinedGrandTotal)}
      </div>
    </div>
  );
}

function EstimatorWorkspace() {
  const [tab, setTab] = useState("sbs");
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-[#111]">
      <div className="w-full max-w-[1400px] mx-auto px-8 pt-5 pb-14">
        {/* Back link */}
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1.5 text-[13px] text-[#5F5E5A] hover:text-[#111] transition-colors mb-5 cursor-pointer"
          data-testid="estimator-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Dashboard
        </button>

        <Header activeTab={tab} onTabChange={setTab} />

        <main data-testid="estimator-main">
          {tab === "modules" && <DataModulesTab />}
          {tab === "illustrations" && <IllustrationsTab />}
          {tab === "reviews" && <ReviewsUpdatesTab />}
          {tab === "sbs" && <SystemBreakdownTab />}
          {tab === "dml" && <DmlTab />}
          {tab === "roles" && <RolesTab />}
          {tab === "resourcing" && <ResourcingTab />}
          {tab === "brex" && <BrexTab />}
          {tab === "risk" && <RiskTab />}
          {tab === "schedule" && <ScheduleTab />}
          {tab === "bidprice" && <BidPriceTab />}
          {tab === "summary" && <SummaryTab />}
          {tab === "readiness" && <ReadinessTab />}
          {tab === "compare" && <CompareTab />}
          {tab === "ratecards" && <RateCardsTab />}
          {tab === "portfolio" && <PortfolioTab />}
          {tab === "projects" && <ProjectsTab onLoaded={() => setTab("modules")} />}
        </main>

        <FooterBar />
      </div>
    </div>
  );
}

export default function ProjectEstimatorPage() {
  return (
    <EstimateProvider>
      <EstimatorWorkspace />
    </EstimateProvider>
  );
}
